import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import LanguageToggle from "../components/LanguageToggle";
import Logo from "../components/Logo";
import { ArrowRight, Building2, Users, Calendar, FileCheck2, ShieldCheck } from "lucide-react";

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const heroImg = "https://images.unsplash.com/photo-1696470553784-4f28d59b305d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwY29uc3RydWN0aW9uJTIwYnVpbGRpbmclMjBmYWNhZGUlMjBnb2xkZW4lMjBob3VyfGVufDB8fHx8MTc4MTExNjQwMHww&ixlib=rb-4.1.0&q=85";

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      {/* Top bar */}
      <header className="absolute top-0 left-0 right-0 z-10 px-6 lg:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="mark" tone="light" className="w-11 h-11 shrink-0 drop-shadow-sm" />
          <div className="text-white font-semibold tracking-wide drop-shadow-sm">Welcome Home</div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          {user ? (
            <Link data-testid="landing-go-portal" to="/app/dashboard" className="sw-btn-primary">{t("app.cta")}</Link>
          ) : (
            <Link data-testid="landing-login-btn" to="/login" className="sw-btn-primary">{t("app.cta")}</Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[80vh] overflow-hidden">
        <img src={heroImg} alt="Hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 sw-hero-overlay" />
        <div className="relative z-10 px-6 lg:px-12 pt-32 pb-24 max-w-[1200px] mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/25 rounded-full px-4 py-1.5 text-white/95 text-xs font-medium tracking-wide uppercase mb-8">
            <ShieldCheck size={14} />
            <span>{t("landing.badge")}</span>
          </div>
          <h1 className="sw-heading text-white text-[44px] md:text-[64px] lg:text-[76px] leading-[1.02] tracking-tight max-w-[1000px]">
            Welcome Home,<br />
            <span className="text-[#e2e8f0] italic font-light">your team.</span>
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            {t("app.tagline")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link data-testid="hero-cta-btn" to={user ? "/app/dashboard" : "/login"} className="sw-btn-primary !px-7 !py-3.5">
              {t("app.cta")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Calendar, title: t("landing.feat_pto"), desc: t("landing.feat_pto_desc") },
            { icon: FileCheck2, title: t("landing.feat_reimb"), desc: t("landing.feat_reimb_desc") },
            { icon: Users, title: t("landing.feat_dir"), desc: t("landing.feat_dir_desc") },
            { icon: Building2, title: t("landing.feat_org"), desc: t("landing.feat_org_desc") },
          ].map((f, i) => (
            <div key={i} className="sw-card sw-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center mb-5">
                <f.icon size={22} />
              </div>
              <h3 className="sw-heading text-[18px] mb-2">{f.title}</h3>
              <p className="text-[14px] text-[#747474] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="px-6 lg:px-12 py-8 text-center text-[12px] text-[#747474] border-t border-[#eeeded]">
        © {new Date().getFullYear()} {t("landing.footer")}
      </footer>
    </div>
  );
}
